import {
  AppstoreOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
  SettingOutlined,
  TagsOutlined,
  TrophyOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Divider,
  Empty,
  Form,
  Image,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Result,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
  theme,
} from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";
import DesignScaleService from "../../services/designScaleService";
import { designScalePermissions } from "../../utils/permissions";
import "./DesignScale.css";

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { TabPane } = Tabs;

// ─── Color mapping for scale status ───
const STATUS_COLORS = {
  "Not Started": "#9CA3AF",
  "In Progress": "#FBBF24",
  Scaled: "#34D399",
  Error: "#EF4444",
};

const WIN_STATUS_COLORS = {
  new: "#3B82F6",
  stable: "#8B5CF6",
};

// ─── Sub-Components ───

function SettingsView({ user }) {
  const [activeTab, setActiveTab] = useState("country");
  const [settings, setSettings] = useState({});
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form] = Form.useForm();
  const hasLoadedSettingsRef = useRef(false);
  const canManage = designScalePermissions.canManage(user);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await DesignScaleService.getSettings();
      setSettings(res.data || {});
    } catch (err) {
      console.error("Failed to load settings:", err);
    }
    try {
      const res = await DesignScaleService.getPendingSettings();
      setPending(res.data || []);
    } catch (err) {
      // ignore
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (hasLoadedSettingsRef.current) return;
    hasLoadedSettingsRef.current = true;
    loadSettings();
  }, [loadSettings]);

  const handleAdd = () => {
    setEditItem(null);
    form.resetFields();
    form.setFieldsValue({ settingType: activeTab === "pending" ? "country" : activeTab });
    setModalVisible(true);
  };

  const handleEdit = (item) => {
    setEditItem(item);
    form.setFieldsValue(item);
    setModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (editItem) {
        await DesignScaleService.updateSetting(editItem.id, values);
        message.success("Updated successfully");
      } else {
        await DesignScaleService.createSetting(values);
        message.success("Created successfully");
      }
      setModalVisible(false);
      loadSettings();
    } catch (err) {
      if (err.errorFields) return;
      message.error(err.response?.data?.message || err.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      await DesignScaleService.deleteSetting(id);
      message.success("Deleted");
      loadSettings();
    } catch (err) {
      message.error(err.response?.data?.message || err.message);
    }
  };

  const handleApprove = async (id) => {
    try {
      await DesignScaleService.approveSetting(id);
      message.success("Approved");
      loadSettings();
    } catch (err) {
      message.error(err.response?.data?.message || err.message);
    }
  };

  const settingTabs = [
    { key: "country", label: "Countries", type: "country" },
    { key: "color", label: "Colors", type: "color" },
    { key: "design_type", label: "Design Types", type: "design_type" },
    { key: "product_type", label: "Product Types", type: "product_type" },
    { key: "market", label: "Markets", type: "market" },
    { key: "scale_status", label: "Scale Statuses", type: "scale_status" },
  ];

  const currentType = activeTab === "pending" ? null : activeTab;
  const dataSource = activeTab === "pending" ? pending : settings[currentType] || [];

  return (
    <div>
      <div className="design-scale-toolbar">
        <Tabs activeKey={activeTab} onChange={setActiveTab} size="small" style={{ marginBottom: 0 }}>
          {settingTabs.map((t) => (
            <TabPane tab={t.label} key={t.key} />
          ))}
          <TabPane
            tab={
              <span>
                <Badge count={pending.length} size="small">
                  {" "}
                  Pending
                </Badge>
              </span>
            }
            key="pending"
          />
        </Tabs>
        {canManage && (
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Add
          </Button>
        )}
      </div>
      <Table
        className="design-scale-table"
        dataSource={dataSource}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        columns={[
          { title: "Value", dataIndex: "settingValue", key: "settingValue" },
          { title: "Display Order", dataIndex: "displayOrder", key: "displayOrder", width: 160 },
          {
            title: "Color",
            dataIndex: "colorHex",
            key: "colorHex",
            width: 80,
            render: (val) => (val ? <div style={{ width: 24, height: 24, background: val, borderRadius: 4, border: "1px solid #d9d9d9" }} /> : "-"),
          },
          {
            title: "Active",
            dataIndex: "isActive",
            key: "isActive",
            width: 80,
            render: (val) => (val ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>),
          },
          {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 100,
            render: (val) => {
              if (val === "pending") return <Tag color="orange">Pending</Tag>;
              return <Tag color="blue">Active</Tag>;
            },
          },
          ...(canManage
            ? [
              {
                title: "Action",
                key: "action",
                width: 160,
                render: (_, record) => (
                  <Space>
                    {record.status === "pending" && (
                      <Button type="link" size="small" icon={<CheckCircleOutlined />} onClick={() => handleApprove(record.id)}>
                        Approve
                      </Button>
                    )}
                    <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                    <Popconfirm title="Delete?" onConfirm={() => handleDelete(record.id)}>
                      <Button type="link" size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </Space>
                ),
              },
            ]
            : []),
        ]}
      />
      <Modal title={editItem ? "Edit setting" : "Add setting"} open={modalVisible} onOk={handleSave} onCancel={() => setModalVisible(false)}>
        <Form form={form} layout="vertical">
          <Form.Item name="settingType" label="Type" rules={[{ required: true }]}>
            <Select>
              <Option value="country">Country</Option>
              <Option value="color">Color</Option>
              <Option value="design_type">Design Type</Option>
              <Option value="product_type">Product Type</Option>
              <Option value="market">Market</Option>
              <Option value="scale_status">Scale Status</Option>
            </Select>
          </Form.Item>
          <Form.Item name="settingValue" label="Value" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="displayOrder" label="Display Order">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="colorHex" label="Color Hex (for status)">
            <Input placeholder="#FFFFFF" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

function MatrixView({ user, embedded, isMatrixActive, urlGroupId, lookupTarget }) {
  const [searchParams] = useSearchParams();
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [matrixData, setMatrixData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([dayjs().subtract(7, "day"), dayjs()]);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);
  const [deletingSkuMappingId, setDeletingSkuMappingId] = useState(null);
  const [savingVariant, setSavingVariant] = useState(false);
  const [variantForm] = Form.useForm();
  const [groupModalVisible, setGroupModalVisible] = useState(false);
  const [groupFormEdit, setGroupFormEdit] = useState(null);
  const [groupForm] = Form.useForm();
  const [allSettings, setAllSettings] = useState({});
  const loadedGroupsKeyRef = useRef(null);
  const hasLoadedSettingsRef = useRef(false);
  const wasMatrixActiveRef = useRef(isMatrixActive);
  const canUpdate = designScalePermissions.canUpdate(user);

  // Check for highlight from URL params
  const highlightVariantId = lookupTarget?.variantId || searchParams.get("highlight");

  const loadGroups = useCallback(async () => {
    try {
      const res = await DesignScaleService.getGroups({ pageSize: 100 });
      const rows = res.data?.rows || [];
      setGroups(rows);
      setSelectedGroup((currentGroup) => {
        if (rows.length === 0) return null;
        if (urlGroupId) {
          // Check if urlGroupId exists in the loaded groups
          const found = rows.some((g) => g.id === urlGroupId);
          return found ? urlGroupId : rows[0].id;
        }
        return rows.some((g) => g.id === currentGroup) ? currentGroup : rows[0].id;
      });
    } catch (err) {
      console.error("Failed to load groups:", err);
    }
  }, [urlGroupId]);

  useEffect(() => {
    const groupsKey = urlGroupId || "default";
    if (loadedGroupsKeyRef.current === groupsKey) return;
    loadedGroupsKeyRef.current = groupsKey;
    loadGroups();
  }, [loadGroups, urlGroupId]);

  useEffect(() => {
    if (lookupTarget?.groupId) {
      setSelectedGroup(lookupTarget.groupId);
    }
  }, [lookupTarget]);

  const handleGroupSave = async () => {
    try {
      const values = await groupForm.validateFields();
      if (groupFormEdit) {
        await DesignScaleService.updateGroup(groupFormEdit.id, values);
        message.success("Group updated");
      }
      setGroupModalVisible(false);
      setGroupFormEdit(null);
      groupForm.resetFields();
      loadGroups();
    } catch (err) {
      if (err.errorFields) return;
      message.error(err.response?.data?.message || err.message);
    }
  };

  const handleGroupEdit = (group) => {
    setGroupFormEdit(group);
    groupForm.setFieldsValue(group);
    setGroupModalVisible(true);
  };

  const handleGroupDelete = async (id) => {
    try {
      await DesignScaleService.deleteGroup(id);
      message.success("Group deleted");
      if (selectedGroup === id) setSelectedGroup(null);
      loadGroups();
    } catch (err) {
      message.error(err.response?.data?.message || err.message);
    }
  };

  const loadMatrix = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateRange && dateRange[0]) {
        params.from = dateRange[0].format("YYYY-MM-DD");
        params.to = dateRange[1].format("YYYY-MM-DD");
      }
      const res = await DesignScaleService.getMatrix(selectedGroup, params);
      setMatrixData(res.data);
    } catch (err) {
      console.error("Failed to load matrix:", err);
    }
    setLoading(false);
  }, [selectedGroup, dateRange]);

  useEffect(() => {
    if (selectedGroup) {
      loadMatrix();
    }
  }, [loadMatrix, selectedGroup]);

  // Load settings for scale_status dropdown
  const loadSettings = useCallback(async () => {
    try {
      const res = await DesignScaleService.getSettings();
      setAllSettings(res.data || {});
    } catch (err) {
      console.error("Failed to load settings:", err);
    }
  }, []);

  useEffect(() => {
    if (hasLoadedSettingsRef.current) return;
    hasLoadedSettingsRef.current = true;
    loadSettings();
  }, [loadSettings]);

  // Reload both groups & matrix when this tab becomes active (e.g. after assigning SKUs or adding groups)
  useEffect(() => {
    const becameActive = isMatrixActive && !wasMatrixActiveRef.current;
    wasMatrixActiveRef.current = isMatrixActive;

    if (becameActive) {
      loadGroups();
      if (selectedGroup) {
        loadMatrix();
      }
    }
  }, [isMatrixActive, selectedGroup, loadMatrix, loadGroups]);

  // selectedCell holds one sub-variant; modal always edits exactly one variant.
  const handleSubVariantClick = (subVariant, cell) => {
    const cellWithSubVariant = { ...subVariant, country: cell.country, color: cell.color };
    setSelectedCell(cellWithSubVariant);
    variantForm.setFieldsValue({
      scaleStatus: subVariant.scaleStatus,
      mockupUrl: subVariant.mockupUrl,
    });
    setModalVisible(true);
  };

  // handleCellClick: when only 1 sub-variant in cell, open it directly.
  // When multiple sub-variants exist, each sub-variant row has its own onClick.
  const handleCellClick = (cell) => {
    if (!cell.variantId) return;
    const subs = cell.subVariants || [];
    if (subs.length === 1) {
      handleSubVariantClick(subs[0], cell);
    }
  };

  const handleVariantSave = async () => {
    if (!selectedCell?.variantId) return;
    try {
      setSavingVariant(true);
      const values = await variantForm.validateFields();
      await DesignScaleService.updateVariant(selectedCell.variantId, values);
      message.success("Variant updated");
      setModalVisible(false);
      loadMatrix();
    } catch (err) {
      if (err.errorFields) return;
      message.error(err.response?.data?.message || err.message);
    } finally {
      setSavingVariant(false);
    }
  };

  const handleSkuMappingDelete = async (mapping) => {
    if (!mapping?.id || deletingSkuMappingId) return;
    setDeletingSkuMappingId(mapping.id);
    try {
      await DesignScaleService.deleteVariantSku(mapping.id);
      setSelectedCell((current) => current ? {
        ...current,
        skus: (current.skus || []).filter((sku) => sku.id !== mapping.id),
        skuCount: Math.max(0, (current.skuCount || 0) - 1),
      } : current);
      message.success(`Removed mapping for ${mapping.sku}`);
      loadMatrix();
    } catch (err) {
      message.error(err.response?.data?.message || err.message || "Failed to remove SKU mapping");
    } finally {
      setDeletingSkuMappingId(null);
    }
  };

  return (
    <div>
      <Row gutter={[16, 12]} className="design-scale-toolbar" align="middle">
        <Col>
          <Select style={{ width: 250 }} placeholder="Select group" value={selectedGroup} onChange={setSelectedGroup} showSearch optionFilterProp="children">
            {groups.map((g) => (
              <Option key={g.id} value={g.id}>
                {g.code} - {g.groupName}
              </Option>
            ))}
          </Select>
        </Col>
        <Col>
          <RangePicker value={dateRange} onChange={setDateRange} allowClear={false} />
        </Col>
        <Col flex="auto" />
        {canUpdate && (
          <Col>
            <Space>
              {selectedGroup && canUpdate && (
                <>
                  <Button
                    type="link"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => {
                      const g = groups.find((x) => x.id === selectedGroup);
                      if (g) handleGroupEdit(g);
                    }}
                  />
                  <Popconfirm title="Delete this group?" onConfirm={() => handleGroupDelete(selectedGroup)}>
                    <Button type="link" size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </>
              )}
            </Space>
          </Col>
        )}
      </Row>

      {loading ? (
        <div style={{ textAlign: "center", padding: 60 }}>
          <Spin size="large" />
        </div>
      ) : matrixData ? (
        <div className="design-scale-matrix-wrap">
          <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}>
            <thead>
              <tr>
                <th style={thStyle}>Country / Color</th>
                {matrixData.axes.colors.map((c) => (
                  <th key={c.value} style={{ ...thStyle, textAlign: "center" }}>
                    {c.value}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrixData.grid.map((row) => (
                <tr key={row.country}>
                  <td style={{ ...tdStyle, fontWeight: 600, background: "#fafafa" }}>{row.country}</td>
                  {row.cells.map((cell) => {
                    const cellHasVariants = (cell.subVariants || []).length > 0;
                    const isHighlighted = highlightVariantId && (cell.subVariants || []).some((sv) => sv.variantId === highlightVariantId);
                    const cellTotalSales = cell.totalSales || 0;
                    const cellSkuCount = cell.skuCount || 0;

                    return (
                      <td
                        key={`${cell.country}-${cell.color}`}
                        style={{
                          ...tdStyle,
                          textAlign: "center",
                          cursor: cellHasVariants && (cell.subVariants || []).length === 1 ? "pointer" : "default",
                          background: isHighlighted ? "#FEF3C7" : cellHasVariants ? "#F9FAFB" : "#F3F4F6",
                          border: isHighlighted ? "2px solid #F59E0B" : "1px solid #E5E7EB",
                          transition: "all 0.2s",
                          minWidth: 140,
                          padding: 0,
                        }}
                        onClick={() => handleCellClick(cell)}
                      >
                        {cellHasVariants ? (
                          <div style={{ padding: "4px 2px" }}>
                            {/* Aggregated totals for the whole cell */}
                            <div style={{ fontWeight: 600, fontSize: 13, color: "#1F2937", paddingTop: 4 }}>
                              {cellTotalSales.toLocaleString()}
                            </div>
                            <div style={{ fontSize: 10, color: "#6B7280", marginBottom: 4 }}>Orders · {cellSkuCount} SKUs</div>

                            {/* One sub-row per sub-variant (no-custom / custom / productType) */}
                            {(cell.subVariants || []).map((sv) => {
                              const svStatusColor = STATUS_COLORS[sv.scaleStatus] || "#E5E7EB";
                              const svIsHighlighted = highlightVariantId && sv.variantId === highlightVariantId;
                              const svThumbnail = sv.skuImageUrl || sv.mockupUrl;
                              const svLabel = sv.isCustom ? "CUSTOM" : "NO-CUSTOM";
                              const svLabelColor = sv.isCustom ? "#3B82F6" : "#6B7280";

                              return (
                                <div
                                  key={sv.variantId}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSubVariantClick(sv, cell);
                                  }}
                                  style={{
                                    cursor: "pointer",
                                    borderTop: "1px solid #E5E7EB",
                                    padding: "5px 4px",
                                    background: svIsHighlighted ? "#FEF9C3" : "transparent",
                                    transition: "background 0.15s",
                                  }}
                                  onMouseEnter={(e) => { e.currentTarget.style.background = svIsHighlighted ? "#FEF9C3" : "#EFF6FF"; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.background = svIsHighlighted ? "#FEF9C3" : "transparent"; }}
                                >
                                  {/* Label row: CUSTOM/NO-CUSTOM + productType */}
                                  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 3, marginBottom: 3 }}>
                                    <span style={{ fontSize: 9, fontWeight: 700, color: svLabelColor, letterSpacing: 0.2 }}>
                                      {svLabel}
                                    </span>
                                    {sv.productType && (
                                      <span style={{ fontSize: 9, color: "#9CA3AF" }}>· {sv.productType}</span>
                                    )}
                                  </div>

                                  {/* Status dot + Win trophy */}
                                  <div style={{ display: "flex", justifyContent: "center", gap: 4, marginBottom: 3 }}>
                                    <div style={{ width: 7, height: 7, borderRadius: "50%", background: svStatusColor }} />
                                    {sv.isWin && (
                                      <Tooltip title={`Win: ${sv.winStatus}`}>
                                        <TrophyOutlined style={{ color: WIN_STATUS_COLORS[sv.winStatus] || "#F59E0B", fontSize: 11 }} />
                                      </Tooltip>
                                    )}
                                  </div>

                                  {/* Thumbnail */}
                                  {svThumbnail ? (
                                    <div onClick={(e) => e.stopPropagation()}>
                                      <Image
                                        src={svThumbnail}
                                        alt="SKU"
                                        style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 4, marginBottom: 3, border: "1px solid #E5E7EB" }}
                                        onError={(e) => { e.target.style.display = "none"; }}
                                      />
                                    </div>
                                  ) : (
                                    <div style={{ width: 40, height: 40, margin: "0 auto 3px", background: "#E5E7EB", borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: "#9CA3AF" }}>
                                      No img
                                    </div>
                                  )}

                                  {/* Per-variant sales */}
                                  <div style={{ fontSize: 11, fontWeight: 600, color: "#374151" }}>{sv.totalSales.toLocaleString()}</div>
                                  <div style={{ fontSize: 9, color: "#9CA3AF" }}>{sv.skuCount} SKUs</div>

                                  {/* Scale status tag */}
                                  {sv.scaleStatus && (
                                    <Tag color={svStatusColor} style={{ fontSize: 9, marginTop: 2, padding: "0 4px" }}>
                                      {sv.scaleStatus}
                                    </Tag>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div style={{ padding: 20, color: "#D1D5DB", fontSize: 11 }}>—</div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>

          {/* Legend */}
          <div style={{ marginTop: 16, display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Legend:
            </Text>
            {Object.entries(STATUS_COLORS).map(([k, v]) => (
              <span key={k} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: v, display: "inline-block" }} />
                {k}
              </span>
            ))}
            <Divider type="vertical" />
            <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12 }}>
              <TrophyOutlined style={{ color: "#F59E0B" }} /> Win
            </span>
          </div>
        </div>
      ) : (
        <Empty description="Select a group to view matrix" />
      )}

      {/* Variant detail modal */}
      <Modal title="Cell Details" open={modalVisible} onOk={handleVariantSave} confirmLoading={savingVariant} onCancel={() => setModalVisible(false)} width={500}>
        {selectedCell && (
          <div>
            <Descriptions column={2} size="small" bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Country">{selectedCell.country}</Descriptions.Item>
              <Descriptions.Item label="Color">{selectedCell.color}</Descriptions.Item>
              <Descriptions.Item label="Product Type">{selectedCell.productType || "-"}</Descriptions.Item>
              <Descriptions.Item label="Custom">{selectedCell.isCustom ? "Yes" : "No"}</Descriptions.Item>
              <Descriptions.Item label="Total Sales">
                <b>{selectedCell.totalSales.toLocaleString()}</b>
              </Descriptions.Item>
              <Descriptions.Item label="SKUs">{selectedCell.skuCount}</Descriptions.Item>
              <Descriptions.Item label="Win Status">{selectedCell.isWin ? <Tag color={WIN_STATUS_COLORS[selectedCell.winStatus]}>{selectedCell.winStatus}</Tag> : "-"}</Descriptions.Item>
            </Descriptions>

            {selectedCell.skus?.length > 0 ? (
              <div style={{ marginBottom: 16 }}>
                <Text strong>Mapped SKUs:</Text>
                <div style={{ maxHeight: 100, overflowY: "auto", marginTop: 4 }}>
                  {selectedCell.skus.map((s) => (
                    <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, padding: "2px 0" }}>
                      <Tag>{s.sku}</Tag>
                      <Text type="secondary" style={{ fontSize: 11, flex: 1 }}>
                        {(s.markets || []).join(", ")}
                      </Text>
                      {canUpdate && (
                        <Popconfirm
                          title="Remove this SKU mapping?"
                          description={`This will return ${s.sku} to Unassigned SKUs.`}
                          okText="Remove"
                          okButtonProps={{ danger: true, loading: deletingSkuMappingId === s.id }}
                          onConfirm={() => handleSkuMappingDelete(s)}
                        >
                          <Button type="text" danger size="small" icon={<DeleteOutlined />} disabled={Boolean(deletingSkuMappingId)} aria-label={`Remove mapping for ${s.sku}`} />
                        </Popconfirm>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ marginBottom: 16 }}>
                <Text strong>Mapped SKUs:</Text>
                <div><Text type="secondary">No mapped SKUs</Text></div>
              </div>
            )}

            <Divider />
            <Text strong>Update Variant</Text>
            <Form form={variantForm} layout="vertical" style={{ marginTop: 8 }}>
              <Form.Item name="scaleStatus" label="Scale Status">
                <Select allowClear placeholder="Select status">
                  {(allSettings.scale_status || []).map((s) => (
                    <Option key={s.id} value={s.settingValue}>
                      {s.settingValue}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="mockupUrl" label="Mockup URL (paste external link)">
                <Input placeholder="https://example.com/mockup.jpg" />
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>

      {/* Group create/edit modal */}
      <Modal
        title={"Edit Template Group"}
        open={groupModalVisible}
        onOk={handleGroupSave}
        onCancel={() => {
          setGroupModalVisible(false);
          setGroupFormEdit(null);
          groupForm.resetFields();
        }}
      >
        <Form form={groupForm} layout="vertical">
          <Form.Item
            name="code"
            label="Code"
            rules={[
              { required: true, message: "Code is required (e.g., T001)" },
              { pattern: /^T\d{3,}$/, message: "Must follow format T001, T002, ..." },
            ]}
          >
            <Input placeholder="T001" />
          </Form.Item>
          <Form.Item name="groupName" label="Group Name" rules={[{ required: true, message: "Group name is required" }]}>
            <Input placeholder="My Template Group" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch defaultChecked />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

const thStyle = {
  padding: "10px 12px",
  border: "1px solid #E5E7EB",
  background: "#F9FAFB",
  fontWeight: 600,
  fontSize: 12,
  color: "#374151",
  whiteSpace: "nowrap",
};

const tdStyle = {
  padding: "4px 6px",
  border: "1px solid #E5E7EB",
  fontSize: 12,
  verticalAlign: "top",
};

function UnassignedSkusView({ user }) {
  const [data, setData] = useState({ rows: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [allSettings, setAllSettings] = useState({});
  const [availableGroups, setAvailableGroups] = useState([]);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [assignForm] = Form.useForm();
  const [createForm] = Form.useForm();
  const [assignTab, setAssignTab] = useState("update");
  const [parseModalVisible, setParseModalVisible] = useState(false);
  const [parseLoading, setParseLoading] = useState(false);
  const [parseResult, setParseResult] = useState(null);
  const hasLoadedInitialDataRef = useRef(false);
  const canManage = designScalePermissions.canManage(user);
  const canUpdate = designScalePermissions.canUpdate(user);

  const loadGroups = useCallback(async () => {
    try {
      const res = await DesignScaleService.getGroups({ pageSize: 200 });
      setAvailableGroups(res.data?.rows || []);
    } catch (err) {
      console.error("Failed to load groups:", err);
    }
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const res = await DesignScaleService.getSettings();
      setAllSettings(res.data || {});
    } catch (err) {
      console.error("Failed to load settings:", err);
    }
  }, []);

  useEffect(() => {
    if (hasLoadedInitialDataRef.current) return;
    hasLoadedInitialDataRef.current = true;
    loadSettings();
    loadGroups();
  }, [loadSettings, loadGroups]);

  // Reload settings & groups each time the assign modal opens to pick up latest changes
  useEffect(() => {
    if (assignModalVisible) {
      loadSettings();
      loadGroups();
    }
  }, [assignModalVisible, loadSettings, loadGroups]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await DesignScaleService.getUnassignedSkus({ page, pageSize: 20, search: debouncedSearch });
      setData(res.data);
    } catch (err) {
      console.error("Failed to load unassigned SKUs:", err);
    }
    setLoading(false);
  }, [page, debouncedSearch]);

  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timeoutId);
  }, [search]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleExport = async () => {
    try {
      const res = await DesignScaleService.exportUnassignedSkus();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `unassigned_skus_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      message.error("Export failed");
    }
  };

  const handlePreviewParse = async () => {
    setParseLoading(true);
    setParseResult(null);
    try {
      const res = await DesignScaleService.previewParse({});
      setParseResult(res.data);
      setParseModalVisible(true);
    } catch (err) {
      message.error(err.response?.data?.message || err.message || "Preview failed");
    }
    setParseLoading(false);
  };

  const handleRunParse = async () => {
    setParseLoading(true);
    try {
      const res = await DesignScaleService.runParse({});
      message.success(`Parse complete: ${res.data?.mapped || 0} SKUs mapped`);
      setParseModalVisible(false);
      setParseResult(null);
      loadData();
    } catch (err) {
      message.error(err.response?.data?.message || err.message || "Parse failed");
    }
    setParseLoading(false);
  };

  const handleBulkAssign = async () => {
    try {
      const form = assignTab === "create" ? createForm : assignForm;
      const values = await form.validateFields();
      if (assignTab === "create") {
        // Tạo mới: cần group name, description, isActive
        await DesignScaleService.bulkAssign({
          skuItemIds: selectedRowKeys,
          groupCode: values.groupCode,
          groupName: values.groupName,
          description: values.description,
          isActive: values.isActive,
          country: values.country,
          color: values.color,
          productType: values.productType,
          isCustom: values.isCustom,
          markets: values.markets,
        });
      } else {
        // Update: chỉ lấy group code đã chọn + assignment fields
        await DesignScaleService.bulkAssign({
          skuItemIds: selectedRowKeys,
          groupCode: values.groupCode,
          country: values.country,
          color: values.color,
          productType: values.productType,
          isCustom: values.isCustom,
          markets: values.markets,
        });
      }
      message.success(`Assigned ${selectedRowKeys.length} SKUs`);
      setAssignModalVisible(false);
      setSelectedRowKeys([]);
      assignForm.resetFields();
      createForm.resetFields();
      loadData();
    } catch (err) {
      if (err.errorFields) return;
      message.error(err.response?.data?.message || err.message);
    }
  };

  const columns = [
    { title: "SKU", dataIndex: "sku", key: "sku", width: 350, ellipsis: true },
    { title: "ASIN", dataIndex: "asin", key: "asin", width: 140 },
    { title: "Title", dataIndex: "title", key: "title", ellipsis: true },
    { title: "Channel", dataIndex: "fulfillmentChannel", key: "fulfillmentChannel", width: 80 },
    { title: "Status", dataIndex: "status", key: "status", width: 80 },
    {
      title: "Seller",
      key: "seller",
      width: 120,
      render: (_, r) => r.sellerAccount?.name || "-",
    },
  ];

  return (
    <div>
      <Row className="design-scale-toolbar" gutter={[8, 8]}>
        <Col span={8}>
          <Input
            placeholder="Search SKU/ASIN/title..."
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            allowClear
          />
        </Col>
        <Col flex="auto" />
        {canManage && (
          <Col>
            <Button icon={<SearchOutlined />} onClick={handlePreviewParse} loading={parseLoading}>
              Parse SKUs
            </Button>
          </Col>
        )}
        <Col>
          <Button icon={<DownloadOutlined />} onClick={handleExport}>
            Export CSV
          </Button>
        </Col>
        {canUpdate && (
          <Col>
            <Button type="primary" disabled={selectedRowKeys.length === 0} onClick={() => setAssignModalVisible(true)}>
              Assign ({selectedRowKeys.length})
            </Button>
          </Col>
        )}
      </Row>

      <Table
        className="design-scale-table"
        dataSource={data.rows || []}
        columns={columns}
        rowKey="id"
        loading={loading}
        rowSelection={
          canUpdate
            ? {
              selectedRowKeys,
              onChange: setSelectedRowKeys,
            }
            : undefined
        }
        pagination={{
          current: page,
          pageSize: 20,
          total: data.total || 0,
          onChange: setPage,
          showTotal: (t) => `${t} unassigned SKUs`,
        }}
        size="small"
      />

      <Modal
        title="Assign SKUs to Design Group"
        open={assignModalVisible}
        onOk={handleBulkAssign}
        onCancel={() => {
          setAssignModalVisible(false);
          assignForm.resetFields();
          createForm.resetFields();
        }}
        width={600}
      >
        <Alert message={`Assigning ${selectedRowKeys.length} SKU(s)`} type="info" showIcon style={{ marginBottom: 16 }} />
        <Tabs
          activeKey={assignTab}
          onChange={(key) => {
            setAssignTab(key);
            // Reset cả 2 form khi chuyển tab để tránh conflict field values
            assignForm.resetFields();
            createForm.resetFields();
          }}
          type="card"
          style={{ marginBottom: 16 }}
        >
          <TabPane tab="Update existing group" key="update">
            <Form form={assignForm} layout="vertical" name="assignForm">
              <Form.Item
                name="groupCode"
                label="Group Code"
                rules={[
                  {
                    required: true,
                    message: "Please select a group",
                  },
                ]}
              >
                <Select showSearch placeholder="Select existing group..." optionFilterProp="children">
                  {availableGroups.map((g) => (
                    <Option key={g.code} value={g.code}>
                      {g.code} — {g.groupName}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="country" label="Country">
                <Select allowClear showSearch placeholder="Select country">
                  {(allSettings.country || []).map((s) => (
                    <Option key={s.id} value={s.settingValue}>
                      {s.settingValue}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="color" label="Color">
                <Select allowClear showSearch placeholder="Select color">
                  {(allSettings.color || []).map((s) => (
                    <Option key={s.id} value={s.settingValue}>
                      {s.settingValue}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="productType" label="Product Type">
                <Select allowClear placeholder="Select product type">
                  {(allSettings.product_type || []).map((s) => (
                    <Option key={s.id} value={s.settingValue}>
                      {s.settingValue}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="isCustom" label="Custom" valuePropName="checked">
                <Switch checkedChildren="CUSTOM" unCheckedChildren="NO-CUSTOM" />
              </Form.Item>
              <Form.Item name="markets" label="Markets" initialValue={["Amazon"]}>
                <Select mode="multiple" placeholder="Select markets">
                  {(allSettings.market || []).map((s) => (
                    <Option key={s.id} value={s.settingValue}>
                      {s.settingValue}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Form>
          </TabPane>
          <TabPane tab="Create new group" key="create">
            <Form form={createForm} layout="vertical" name="createForm">
              <Form.Item
                name="groupCode"
                label="Group Code"
                help="Enter a new code (e.g. T003) — must follow T001, T002, ... format"
                rules={[
                  { required: true, message: "Group code is required" },
                  { pattern: /^T\d{3,}$/, message: "Must follow format T001, T002, ..." },
                  {
                    validator: (_, value) => {
                      if (value && availableGroups.some((g) => g.code === value)) {
                        return Promise.reject(new Error("This code already exists. Use the Update tab instead."));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input placeholder="T003" />
              </Form.Item>
              <Form.Item name="groupName" label="Group Name" rules={[{ required: true, message: "Group name is required" }]}>
                <Input placeholder="My Template Group" />
              </Form.Item>
              <Form.Item name="description" label="Description">
                <Input.TextArea rows={2} />
              </Form.Item>
              <Form.Item name="isActive" label="Active" valuePropName="checked" initialValue={true}>
                <Switch defaultChecked />
              </Form.Item>
              <Divider>Assignment Details</Divider>
              <Form.Item name="country" label="Country">
                <Select allowClear showSearch placeholder="Select country">
                  {(allSettings.country || []).map((s) => (
                    <Option key={s.id} value={s.settingValue}>
                      {s.settingValue}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="color" label="Color">
                <Select allowClear showSearch placeholder="Select color">
                  {(allSettings.color || []).map((s) => (
                    <Option key={s.id} value={s.settingValue}>
                      {s.settingValue}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="productType" label="Product Type">
                <Select allowClear placeholder="Select product type">
                  {(allSettings.product_type || []).map((s) => (
                    <Option key={s.id} value={s.settingValue}>
                      {s.settingValue}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="isCustom" label="Custom" valuePropName="checked">
                <Switch checkedChildren="CUSTOM" unCheckedChildren="NO-CUSTOM" />
              </Form.Item>
              <Form.Item name="markets" label="Markets" initialValue={["Amazon"]}>
                <Select mode="multiple" placeholder="Select markets">
                  {(allSettings.market || []).map((s) => (
                    <Option key={s.id} value={s.settingValue}>
                      {s.settingValue}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Form>
          </TabPane>
        </Tabs>
      </Modal>

      {/* Parse SKUs modal */}
      <Modal
        title="Parse Unassigned SKUs"
        open={parseModalVisible}
        onOk={handleRunParse}
        onCancel={() => {
          setParseModalVisible(false);
          setParseResult(null);
        }}
        confirmLoading={parseLoading}
        okText="Run Parse"
        width={600}
      >
        {parseLoading && !parseResult ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin />
            <p style={{ marginTop: 16, color: "#6B7280" }}>Previewing SKUs to parse...</p>
          </div>
        ) : parseResult ? (
          <div>
            <Alert message={`Preview: ${parseResult.total || 0} SKUs to parse`} type="info" showIcon style={{ marginBottom: 16 }} />
            <Descriptions column={2} size="small" bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Total SKUs">{parseResult.total || 0}</Descriptions.Item>
              <Descriptions.Item label="Will map to">{parseResult.willMap || 0} variants</Descriptions.Item>
              <Descriptions.Item label="New groups">{parseResult.newGroups || 0}</Descriptions.Item>
              <Descriptions.Item label="New variants">{parseResult.newVariants || 0}</Descriptions.Item>
              <Descriptions.Item label="Skipped (invalid format)">{parseResult.skipped || 0}</Descriptions.Item>
              <Descriptions.Item label="Pending values">{parseResult.pendingValues || 0}</Descriptions.Item>
            </Descriptions>

            {parseResult.invalidSamples?.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <Text strong>Invalid format samples:</Text>
                <div style={{ maxHeight: 120, overflowY: "auto", marginTop: 4 }}>
                  {parseResult.invalidSamples.map((s, i) => (
                    <Tag key={i} color="red" style={{ marginBottom: 4 }}>
                      {s}
                    </Tag>
                  ))}
                </div>
              </div>
            )}

            <Text type="warning" style={{ fontSize: 12 }}>
              ⚠️ This will create/update template groups, design variants, and map SKUs automatically.
            </Text>
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: 40, color: "#9CA3AF" }}>
            <Text>Click "Parse SKUs" button to preview first, then click "Run Parse" to execute.</Text>
          </div>
        )}
      </Modal>
    </div>
  );
}

function LookupResult({ result, sku, onGoToUnassigned, onOpenInMatrix }) {
  return (
    <div>
      {result && (
        <div>
          {result.found === false ? (
            <Result status="warning" title="SKU not found" subTitle={`SKU "${sku}" not in system`} />
          ) : result.unassigned ? (
            <Result
              status="info"
              title="SKU not assigned"
              subTitle={`SKU "${sku}" is found but not yet assigned to any design group. Please assign it first.`}
              extra={[
                <Button key="go-unassigned" type="primary" icon={<UnorderedListOutlined />} onClick={() => onGoToUnassigned?.()}>
                  Go to Unassigned
                </Button>,
              ]}
            />
          ) : (
            <div>
              <Card
                title={
                  <>
                    <TagsOutlined /> {result.templateGroup?.code} — {result.templateGroup?.groupName}
                  </>
                }
                style={{ marginBottom: 16 }}
              >
                <Descriptions column={2} size="small" bordered>
                  <Descriptions.Item label="Matched Variant ID">{result.matchedVariantId}</Descriptions.Item>
                  <Descriptions.Item label="Total Variants">{result.variants?.length || 0}</Descriptions.Item>
                </Descriptions>
                <div style={{ marginTop: 12 }}>
                  <Button type="primary" size="small" icon={<AppstoreOutlined />} onClick={() => onOpenInMatrix(result.templateGroup?.id, result.matchedVariantId)}>
                    View in Matrix
                  </Button>
                  <Button
                    type="link"
                    size="small"
                    icon={<DownloadOutlined />}
                    style={{ marginLeft: 8 }}
                    onClick={() => {
                      const url = `${window.location.origin}/optimization/sku-template-group/${result.templateGroup?.id}?highlight=${result.matchedVariantId}`;
                      navigator.clipboard.writeText(url);
                      message.success("Link copied!");
                    }}
                  >
                    Copy link
                  </Button>
                </div>
              </Card>

              <Table
                className="design-scale-table"
                dataSource={result.variants || []}
                rowKey="id"
                size="small"
                pagination={false}
                columns={[
                  { title: "Country", dataIndex: "country", width: 100 },
                  { title: "Color", dataIndex: "color", width: 100 },
                  { title: "Product Type", dataIndex: "productType", width: 100 },
                  {
                    title: "Custom",
                    dataIndex: "isCustom",
                    width: 80,
                    render: (v) => (v ? <Tag color="blue">CUSTOM</Tag> : <Tag>NO-CUSTOM</Tag>),
                  },
                  { title: "Status", dataIndex: "scaleStatus", width: 120 },
                  {
                    title: "Win",
                    dataIndex: "isWin",
                    width: 80,
                    render: (v, r) => (v ? <Tag color={WIN_STATUS_COLORS[r.winStatus]}>{r.winStatus}</Tag> : "-"),
                  },
                  {
                    title: "Markets",
                    key: "markets",
                    width: 180,
                    render: (_, r) =>
                      (r.skus || [])
                        .flatMap((s) => s.markets || [])
                        .filter((v, i, a) => a.indexOf(v) === i)
                        .join(", ") || "-",
                  },
                  {
                    title: "SKUs",
                    key: "skus",
                    render: (_, r) => (r.skus || []).map((s) => s.sku).join(", "),
                  },
                ]}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function WinCriteriaView({ user }) {
  const [data, setData] = useState({ rows: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form] = Form.useForm();
  const canManage = designScalePermissions.canManage(user);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await DesignScaleService.getWinCriteria({ pageSize: 50 });
      setData(res.data);
    } catch (err) {
      console.error("Failed to load win criteria:", err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (editItem) {
        await DesignScaleService.updateWinCriteria(editItem.id, values);
        message.success("Updated");
      } else {
        await DesignScaleService.createWinCriteria(values);
        message.success("Created");
      }
      setModalVisible(false);
      loadData();
    } catch (err) {
      if (err.errorFields) return;
      message.error(err.response?.data?.message || err.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      await DesignScaleService.deleteWinCriteria(id);
      message.success("Deleted");
      loadData();
    } catch (err) {
      message.error(err.response?.data?.message || err.message);
    }
  };

  const handleEdit = (item) => {
    setEditItem(item);
    form.setFieldsValue({
      ...item,
      seasonStart: item.seasonStart ? dayjs(item.seasonStart) : null,
      seasonEnd: item.seasonEnd ? dayjs(item.seasonEnd) : null,
    });
    setModalVisible(true);
  };

  const columns = [
    { title: "Name", dataIndex: "name", key: "name", width: 180 },
    { title: "Scope", dataIndex: "scopeType", key: "scopeType", width: 80 },
    { title: "New Win (age ≤ / perf ≥)", key: "newWin", width: 160, render: (_, r) => `${r.newWinMaxAgeDays}d / ${r.newWinMinPerf}` },
    { title: "Stable (age > / total ≥ / perf ≥)", key: "stableWin", width: 200, render: (_, r) => `${r.stableWinMinAgeDays}d / ${r.stableWinMinTotalSales} / ${r.stableWinMinPerf}` },
    { title: "Decline (days)", dataIndex: "declineDays", key: "declineDays", width: 100 },
    { title: "Priority", dataIndex: "priority", key: "priority", width: 70 },
    {
      title: "Active",
      dataIndex: "isActive",
      key: "isActive",
      width: 70,
      render: (v) => (v ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>),
    },
    ...(canManage
      ? [
        {
          title: "Action",
          key: "action",
          width: 120,
          render: (_, record) => (
            <Space>
              <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
              <Popconfirm title="Delete?" onConfirm={() => handleDelete(record.id)}>
                <Button type="link" size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </Space>
          ),
        },
      ]
      : []),
  ];

  return (
    <div>
      <div className="design-scale-actions">
        {canManage && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditItem(null);
              form.resetFields();
              setModalVisible(true);
            }}
          >
            Add Criteria
          </Button>
        )}
      </div>

      <Table className="design-scale-table" dataSource={data.rows || []} columns={columns} rowKey="id" loading={loading} pagination={{ pageSize: 20 }} size="small" />

      <Modal title={editItem ? "Edit Win Criteria" : "Add Win Criteria"} open={modalVisible} onOk={handleSave} onCancel={() => setModalVisible(false)} width={600}>
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="scopeType" label="Scope" rules={[{ required: true }]}>
                <Select>
                  <Option value="global">Global</Option>
                  <Option value="group">Group</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="priority" label="Priority">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="isActive" label="Active" valuePropName="checked">
                <Switch defaultChecked />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="seasonStart" label="Season Start">
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="seasonEnd" label="Season End">
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Divider>New Win Criteria</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="newWinMaxAgeDays" label="Max Age (days)" rules={[{ required: true }]}>
                <InputNumber min={1} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="newWinMinPerf" label="Min Performance (sales/day)" rules={[{ required: true }]}>
                <InputNumber min={0} step={0.1} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Divider>Stable Win Criteria</Divider>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="stableWinMinAgeDays" label="Min Age (days)" rules={[{ required: true }]}>
                <InputNumber min={1} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="stableWinMinTotalSales" label="Min Total Sales" rules={[{ required: true }]}>
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="stableWinMinPerf" label="Min Perf" rules={[{ required: true }]}>
                <InputNumber min={0} step={0.1} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Divider />
          <Form.Item name="declineDays" label="Decline Detection (days)" rules={[{ required: true }]}>
            <InputNumber min={1} style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

// ─── Main Container ───

const DesignScale = ({ embedded, user: propUser }) => {
  const { user: authUser } = useSelector((state) => state.auth);
  const user = propUser || authUser;
  const [searchParams] = useSearchParams();
  const { groupId: urlGroupId } = useParams();
  const [activeView, setActiveView] = useState("matrix");
  const [lookupSku, setLookupSku] = useState("");
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupTarget, setLookupTarget] = useState(null);
  const lookupDebounceRef = useRef(null);
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const canRead = designScalePermissions.canRead(user);
  const canManage = designScalePermissions.canManage(user);

  const handleLookup = async (sku = lookupSku) => {
    const normalizedSku = sku.trim();
    if (!normalizedSku) return;

    setLookupLoading(true);
    try {
      const res = await DesignScaleService.lookupSku(normalizedSku);
      const data = res.data;

      // Found & assigned → auto-navigate to matrix with highlight
      if (data.found !== false && !data.unassigned) {
        const groupId = data.templateGroup?.id;
        const variantId = data.matchedVariantId;
        if (groupId && variantId) {
          message.success(`Found SKU "${normalizedSku}" — opening matrix`);
          setLookupTarget({ groupId, variantId });
          setActiveView("matrix");
          setLookupResult(null);
          setLookupSku("");
          return;
        }
      }

      // Otherwise show result inline (unassigned / not found)
      setLookupResult(data);
    } catch (err) {
      message.error(err.response?.data?.message || err.message);
    } finally {
      setLookupLoading(false);
    }
  };

  const handleOpenLookupInMatrix = (groupId, variantId) => {
    setLookupTarget({ groupId, variantId: variantId || null });
    setActiveView("matrix");
  };

  const handleLookupInputChange = (value) => {
    setLookupSku(value);
    setLookupResult(null);
    if (lookupDebounceRef.current) clearTimeout(lookupDebounceRef.current);

    const normalizedSku = value.trim();
    if (!normalizedSku) return;

    lookupDebounceRef.current = setTimeout(() => {
      lookupDebounceRef.current = null;
      handleLookup(normalizedSku);
    }, 300);
  };

  const handleLookupSubmit = (value) => {
    if (lookupDebounceRef.current) {
      clearTimeout(lookupDebounceRef.current);
      lookupDebounceRef.current = null;
    }
    handleLookup(value);
  };

  useEffect(() => () => {
    if (lookupDebounceRef.current) clearTimeout(lookupDebounceRef.current);
  }, []);

  // If there's a highlight/sku param or groupId in URL, default to matrix
  useEffect(() => {
    if (searchParams.get("highlight") || searchParams.get("sku") || urlGroupId) {
      setActiveView("matrix");
    }
  }, [searchParams, urlGroupId]);

  if (!canRead) {
    return (
      <div style={{ margin: "24px 16px" }}>
        <Result status="403" title="403" subTitle="You don't have permission to access this page." />
      </div>
    );
  }

  const views = [
    {
      key: "matrix",
      label: (
        <span>
          <AppstoreOutlined /> Matrix
        </span>
      ),
      component: (
        <MatrixView
          user={user}
          embedded={embedded}
          isMatrixActive={activeView === "matrix"}
          urlGroupId={urlGroupId}
          lookupTarget={lookupTarget}
        />
      ),
    },
    {
      key: "unassigned",
      label: (
        <span>
          <UnorderedListOutlined /> Unassigned SKUs
        </span>
      ),
      component: <UnassignedSkusView user={user} />,
    },
    ...(canManage
      ? [
        {
          key: "settings",
          label: (
            <span>
              <SettingOutlined /> Settings
            </span>
          ),
          component: <SettingsView user={user} />,
        },
        {
          key: "win-criteria",
          label: (
            <span>
              <TrophyOutlined /> Win Criteria
            </span>
          ),
          component: <WinCriteriaView user={user} />,
        },
      ]
      : []),
  ];

  return (
    <div className={`design-scale${embedded ? " design-scale--embedded" : ""}`} style={{ margin: embedded ? 0 : "24px 16px" }}>
      {!embedded && (
        <div style={{ marginBottom: 16 }}>
          <Title level={4} style={{ margin: 0 }}>
            SKU Template Group
          </Title>
          <Text type="secondary">Design scale management & SKU template group mapping</Text>
        </div>
      )}

      <div className="design-scale-panel" style={{ borderRadius: borderRadiusLG }}>
        <div className="design-scale-lookup" style={{ background: colorBgContainer, borderRadius: borderRadiusLG }}>
          <div className="design-scale-lookup__label">
            <Text strong>Quick SKU Lookup</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Find a SKU and open its matrix
            </Text>
          </div>
          <Input.Search
            placeholder="Enter SKU to lookup..."
            value={lookupSku}
            onChange={(e) => handleLookupInputChange(e.target.value)}
            onSearch={handleLookupSubmit}
            enterButton={
              <>
                <SearchOutlined /> Lookup
              </>
            }
            size="large"
            loading={lookupLoading}
            allowClear
            className="design-scale-lookup__input"
          />
        </div>

        {lookupResult && (
          <div className="design-scale-lookup-result">
            <LookupResult
              result={lookupResult}
              sku={lookupSku}
              onGoToUnassigned={() => setActiveView("unassigned")}
              onOpenInMatrix={handleOpenLookupInMatrix}
            />
          </div>
        )}

        <Tabs activeKey={activeView} onChange={setActiveView} type="card" tabBarGutter={5} className="design-scale-tabs">
          {views.map((v) => (
            <TabPane tab={v.label} key={v.key}>
              {v.component}
            </TabPane>
          ))}
        </Tabs>
      </div>
    </div>
  );
};

export default DesignScale;
